import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { View, Keyboard } from 'react-native';
import PropTypes from 'prop-types';
import { ActivityIndicator, Picker, Button, NoDataText, CancelableTextInput } from 'LocalGluons';
import { t } from 'Locale';
import favoritesInputProps from 'Pages/FavoriteSettings/utils/favoritesInputProps';
import { Device } from 'DeviceUtils';
import { useNavigation } from '@react-navigation/native';
import { FavoritePageWrapper } from '../FavoritePageWrapper';
import ChannelFavoriteList from '../../components/ChannelFavoriteList';
import baseStyles, { dropDownIconSize, dropDownWidth } from './ManageFavorites.jss';
import { useCategories } from './useCategories';
import { useFavoriteSettingsContext } from '../../FavoriteSettingsContext';
import HighlightButton from './components/HighlightButton';

const getCategoryName = ({ title, favoritesCount }) =>
    `${t(title)}${favoritesCount > 0 ? ` (${favoritesCount})` : ''}`;

const ManageFavoritesTablet = ({ screenProps: { onSaveCallback, onCancelCallback } }) => {
    const navigation = useNavigation();
    const { orderedFavoriteList, setOrderedFavoriteList, channelMap } =
        useFavoriteSettingsContext();
    const [configLoaded, categories] = useCategories(orderedFavoriteList);
    const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
    const [pickerOpened, setPickerOpened] = useState(false);
    const [query, setQuery] = useState('');
    const navWillBlurListener = useRef({});

    const onOpenCallback = useCallback(() => setPickerOpened(true), []);

    const onCloseCallback = useCallback(() => setPickerOpened(false), []);

    const onChangeSearchTextCallback = useCallback((text) => setQuery(text.toLowerCase()), []);

    const onCategoryPickerValueChangeCallback = useCallback(
        (value) => {
            const newActiveCategoryIndex = categories.findIndex(
                (category) => category.value === value
            );
            if (newActiveCategoryIndex !== -1) {
                setActiveCategoryIndex(newActiveCategoryIndex);
            }
        },
        [categories]
    );

    const queryFilter = useCallback(
        (channel) => channel.name.toLowerCase().includes(query),
        [query]
    );

    const channelsByCategory = useMemo(
        () =>
            categories.map((category) =>
                category.channelIds
                    .map(
                        (chId) =>
                            channelMap[chId] && {
                                ...channelMap[chId],
                                channelId: chId,
                                favorite: orderedFavoriteList.indexOf(chId) !== -1
                            }
                    )
                    .filter(Boolean)
            ),
        [categories, channelMap, orderedFavoriteList]
    );

    useEffect(() => {
        if (channelsByCategory[activeCategoryIndex]?.length) return;
        const firstPopulatedIndex = channelsByCategory.findIndex(
            (categoryChannels) => categoryChannels.length
        );
        if (firstPopulatedIndex >= 0) setActiveCategoryIndex(firstPopulatedIndex);
    }, [channelsByCategory]);

    useEffect(() => {
        navWillBlurListener.current = navigation.addListener('blur', () => {
            Keyboard.dismiss();
        });

        return () => {
            navWillBlurListener?.current();
        };
    }, []);

    const activeCategory = categories[activeCategoryIndex] || {};
    const filteredData = channelsByCategory[activeCategoryIndex]
        ? channelsByCategory[activeCategoryIndex].filter(queryFilter)
        : [];

    const selectedAll = useMemo(() => {
        const filteredDataIds = filteredData.map((item) => item.channelId);
        const filteredFavoriteList = orderedFavoriteList.filter((item) =>
            filteredDataIds.includes(item)
        );
        return filteredData.length === filteredFavoriteList.length;
    }, [orderedFavoriteList, filteredData]);

    if (configLoaded && categories.length === 0) return <NoDataText />;

    return (
        <FavoritePageWrapper
            activeCategory={activeCategoryIndex}
            onSelectAllCallback={() => {
                if (filteredData.length > 0) {
                    const channelIds = filteredData.map((ch) => ch.channelId);
                    setOrderedFavoriteList([...orderedFavoriteList, ...channelIds]);
                }
            }}
            onDeselectAllCallback={() => {
                setOrderedFavoriteList(
                    orderedFavoriteList.filter(
                        (ch) => !filteredData.some((fCh) => fCh.channelId === ch)
                    )
                );
            }}
            onSaveCallback={onSaveCallback}
            onCancelCallback={onCancelCallback}
            selectedAll={selectedAll}
        >
            <View style={baseStyles.tabletTopBar}>
                <View style={{ width: dropDownWidth }}>
                    <Picker
                        mode="dropdown"
                        onValueChange={onCategoryPickerValueChangeCallback}
                        selectedValue={activeCategory.title}
                        menuContainerStyle={baseStyles.categoryDropdown}
                        onOpen={onOpenCallback}
                        onClose={onCloseCallback}
                        buttonWidth={dropDownWidth}
                        portal={!Device.isWeb}
                    >
                        <Picker.Button>
                            <Button
                                style={{
                                    button: [
                                        baseStyles.pickerButton,
                                        pickerOpened ? baseStyles.pickerButtonOpen : {},
                                        { maxHeight: 55, minHeight: 55, height: 55 }
                                    ],
                                    text: [
                                        baseStyles.pickerButtonText,
                                        pickerOpened ? baseStyles.pickerButtonTextOpen : {}
                                    ],
                                    icon: {
                                        width: dropDownIconSize
                                    },
                                    iconContainer: { maxHeight: 55, minHeight: 55, height: 55 }
                                }}
                                ellipsizeMode="middle"
                                isSecondary
                                isDropdown
                                isToggled={pickerOpened}
                                title={getCategoryName(activeCategory)}
                            />
                        </Picker.Button>
                        {categories.map((item, index) => {
                            const isSelected = activeCategory.value === item.value;
                            const isEmpty = channelsByCategory[index].length === 0;
                            if (isSelected || isEmpty) return undefined;

                            return (
                                <Picker.Item value={item.value} label={item.title} key={item.value}>
                                    <HighlightButton
                                        underlayColor="#A4A4A4"
                                        style={{
                                            button: baseStyles.pickerItemButton,
                                            text: baseStyles.pickerItemText,
                                            textHighlight: { color: '#EBEBEB' }
                                        }}
                                        onPress={() => {}}
                                        title={getCategoryName(item)}
                                    />
                                </Picker.Item>
                            );
                        })}
                    </Picker>
                </View>
                <View style={baseStyles.searchMarginContainer}>
                    <CancelableTextInput
                        {...{
                            ...favoritesInputProps,
                            placeholder: t('SETTINGS.FAVOURITE_CHANNELS_SEARCH'),
                            onChangeText: onChangeSearchTextCallback,
                            displayCancelIcon: Boolean(query),
                            onCancel: (inputRef) => {
                                setQuery('');
                                inputRef.clear();
                            }
                        }}
                    />
                </View>
            </View>
            <ChannelFavoriteList
                data={filteredData}
                onItemPress={(item) => {
                    const { channelId, favorite } = item;
                    const neworderedFavoriteList = favorite
                        ? // If it is favorite, filter out if already there
                          orderedFavoriteList.filter((chId) => chId !== channelId)
                        : // else concat channel Id
                          [...orderedFavoriteList, channelId];
                    setOrderedFavoriteList(neworderedFavoriteList);
                }}
            />
            {!configLoaded && <ActivityIndicator />}
        </FavoritePageWrapper>
    );
};

ManageFavoritesTablet.propTypes = {
    screenProps: PropTypes.shape({
        onSaveCallback: PropTypes.func,
        onCancelCallback: PropTypes.func
    }).isRequired
};

export { ManageFavoritesTablet };
